import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import type { BaseVideoFormData, ITraining } from '../../types';
import {
    fetchBonusVideos,
    addBonusVideo,
    editBonusVideo,
    deleteBonusVideo,
    saveAllBonusVideos,
    setSelectedVideo,
    setEditingVideo,
    setOpenDialog,
    setDeleteDialog,
    reorderVideos as reorderBonusVideos,
    updateVideoFromStatus,
    selectHasOrderChanges
} from '../slices/bonusVideosSlice';
import { useNotification } from '../../components/shared/NotificationSystem';
import { useTranslation } from 'react-i18next';
import { useLanguage as useLanguageContext } from '../../i18n/LanguageContext';
import { getCookie } from '../../utils/cookieUtils';

export const useBonusVideos = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { t, i18n } = useTranslation();
    const { language: contextLanguage } = useLanguageContext();
    const { showSuccess, showError, showWarning } = useNotification();

    const {
        videos,
        selectedVideo,
        editingVideo,
        isLoading,
        isSaving,
        error,
        deleteDialog,
        openDialog
    } = useSelector((state: RootState) => state.bonusVideos);

    // Проверяем, есть ли изменения в порядке видео
    const hasOrderChanges = useSelector(selectHasOrderChanges);

    // Используем язык из контекста или куки вместо admin_language
    const language = contextLanguage || getCookie('language') || getCookie('i18next') || 'ru';

    // Load bonus videos
    const loadBonusVideos = useCallback(() => {
        // Синхронизируем язык i18n с текущим языком приложения
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        dispatch(fetchBonusVideos(language));
    }, [dispatch, language, i18n]);

    // Handle form submission for adding/editing
    const handleSubmit = useCallback(async (data: BaseVideoFormData) => {
        try {
            if (editingVideo) {
                await dispatch(editBonusVideo({
                    id: editingVideo.id,
                    training: data.training,
                    language,
                    videoFile: data.videoFile,
                    thumbnailFile: data.thumbnailFile
                }));
                showSuccess(t('content.bonus.success.updated'));
                return null; // Редактирование не создает новое видео
            } else {
                const result = await dispatch(addBonusVideo({
                    training: data.training,
                    language,
                    videoFile: data.videoFile,
                    thumbnailFile: data.thumbnailFile
                }));
                showSuccess(t('content.bonus.success.added'));
                closeDialog();
                // Refresh video list after adding/editing
                await dispatch(fetchBonusVideos(language));
                return result; // Возвращаем результат для отслеживания нового видео
            }
        } catch (error) {
            showError(t('content.bonus.errors.saveFailed'));
            throw error;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, editingVideo, language, t, showSuccess, showError]);

    // Edit a video
    const handleEdit = useCallback((video: ITraining) => {
        dispatch(setEditingVideo(video));
    }, [dispatch]);

    // Delete a video
    const handleDelete = useCallback((videoId: number) => {
        // Find the video to display its title in the confirmation dialog
        const video = videos.find(v => v.id === videoId);

        if (!video) {
            console.error(`Video with ID ${videoId} not found`);
            return;
        }

        // Check language
        if (video.language && video.language.toUpperCase() !== language.toUpperCase()) {
            showWarning(t('content.bonus.warnings.wrongLanguage', {
                videoLang: video.language,
                currentLang: language.toUpperCase()
            }));
            return;
        }

        // Open the delete dialog
        dispatch(setDeleteDialog({
            open: true,
            videoId,
            title: video.title
        }));
    }, [dispatch, videos, language, t, showWarning]);

    // Confirm video deletion
    const confirmDelete = useCallback(async () => {
        if (!deleteDialog.videoId) return;

        try {
            await dispatch(deleteBonusVideo(deleteDialog.videoId));
            showSuccess(t('content.bonus.success.deleted'));
            // Refresh video list after deleting
            await dispatch(fetchBonusVideos(language));
        } catch (error) {
            showError(t('content.bonus.errors.deleteFailed'));
        }
    }, [dispatch, deleteDialog.videoId, t, showSuccess, showError, language]);

    // Close dialog
    const closeDialog = useCallback(() => {
        dispatch(setOpenDialog(false));
    }, [dispatch]);

    // Select video for preview
    const selectVideo = useCallback((video: ITraining) => {
        dispatch(setSelectedVideo(video));
    }, [dispatch]);

    // Close video preview
    const closeVideoPreview = useCallback(() => {
        dispatch(setSelectedVideo(null));
    }, [dispatch]);

    // Save all videos
    const handleSaveAll = useCallback(async () => {
        try {
            await dispatch(saveAllBonusVideos());
            showSuccess(t('content.bonus.success.allSaved'));
            // Refresh video list after saving all
            await dispatch(fetchBonusVideos(language));
        } catch (error) {
            showError(t('content.bonus.errors.saveAllFailed'));
        }
    }, [dispatch, t, showSuccess, showError, language]);

    const openAddDialog = useCallback(() => {
        dispatch(setOpenDialog(true));
    }, [dispatch]);

    const closeDeleteDialog = useCallback(() => {
        dispatch(setDeleteDialog({ open: false, videoId: null, title: '' }));
    }, [dispatch]);

    // Reorder videos
    const reorderVideos = useCallback((activeId: string, overId: string) => {
        dispatch(reorderBonusVideos({ activeId, overId }));
    }, [dispatch]);

    // Update video from status
    const handleUpdateVideoFromStatus = useCallback((videoId: number, updates: Partial<ITraining>) => {
        dispatch(updateVideoFromStatus({ videoId, updates }));
    }, [dispatch]);

    return {
        // State
        videos,
        selectedVideo,
        editingVideo,
        isLoading,
        isSaving,
        error,
        deleteDialog,
        openDialog,
        language,
        hasOrderChanges,

        // Actions
        loadBonusVideos,
        handleSubmit,
        handleEdit,
        handleDelete,
        confirmDelete,
        closeDialog,
        selectVideo,
        closeVideoPreview,
        handleSaveAll,
        openAddDialog,
        closeDeleteDialog,
        reorderVideos,
        updateVideoFromStatus: handleUpdateVideoFromStatus
    };
}; 